import { AlertCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/store/auth';

export function CallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handleCallback = useAuthStore((state) => state.handleCallback);
  const [error, setError] = useState<string | null>(null);

  // Prevent double-invocation in React Strict Mode
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      const description =
        searchParams.get('error_description') || 'Authentication was cancelled or failed.';
      setError(description);
      return;
    }

    if (!code || !state) {
      setError('Missing required parameters from the authentication provider.');
      return;
    }

    handleCallback(code, state)
      .then(() => {
        navigate('/dashboard', { replace: true });
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'Authentication failed. Please try again.';
        setError(message);
      });
  }, [searchParams, handleCallback, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-300 dark:border-neutral-700 shadow-xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
              Sign In Failed
            </h2>
            <p
              className="text-neutral-600 dark:text-neutral-400 mb-6 text-sm"
              data-testid="callback-error"
            >
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="mt-4 text-neutral-400 text-sm" data-testid="callback-loading">
          Completing sign in…
        </p>
      </div>
    </div>
  );
}
